import { DocumentFileList } from '@/common/components/DocumentFileList'
import { MultipleDocumentSoftUpload } from '@/common/components/MultipleDocumentSoftUpload'
import useBlobs from '@/common/hooks/queries/useBlobs'
import { ErrorsMapper } from '@/common/services/errorsMapper'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { useCreatePublicCompanyLeadGenericDocument } from '@/features/publicCompanyLeadsFunnel/hooks/useCreatePublicCompanyLeadGenericDocument'
import { useDeletePublicCompanyLeadGenericDocument } from '@/features/publicCompanyLeadsFunnel/hooks/useDeletePublicCompanyLeadGenericDocument'
import { usePublicCompanyLead } from '@/features/publicCompanyLeadsFunnel/hooks/usePublicCompanyLead'
import { PublicCompanyLeadsFunnelUpdateValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema'
import { PublicCompanyLeadData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'

interface GenericDocumentsSectionProps {
  publicCompanyLeadId?: string
  generic_documents: PublicCompanyLeadData['generic_documents']
  disabled?: boolean
}

export const GenericDocumentsSection = ({
  publicCompanyLeadId,
  generic_documents,
  disabled,
}: GenericDocumentsSectionProps) => {
  const queryClient = useQueryClient()
  const [serverError, setServerError] = useState('')

  const { control } = useFormContext<PublicCompanyLeadsFunnelUpdateValidationSchema>()

  const { isFetching } = usePublicCompanyLead(publicCompanyLeadId || '', false)

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs({
    onSuccess: (response) => {
      createPublicCompanyLeadGenericDocument({ blob_id: response.data.data.id })
    },
  })

  const { mutate: createPublicCompanyLeadGenericDocument, isPending: createPublicCompanyLeadIsPending } =
    useCreatePublicCompanyLeadGenericDocument({
      publicCompanyLeadId,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLeadId] })
        setServerError('')
      },
      onError: (error) => {
        const transformedErrors = ErrorsMapper.default()(error?.response?.data.errors)

        !!transformedErrors.length &&
          setServerError(transformedErrors.map((error) => error.generic_documents).join(', '))
      },
    })

  const { mutate: deletePublicCompanyLeadGenericDocument, isPending: deletePublicCompanyLeadIsPending } =
    useDeletePublicCompanyLeadGenericDocument({
      publicCompanyLeadId,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLeadId] })
      },
    })

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[18px] font-bold">Anexos</p>
      {serverError && <p className="text-sm text-danger-soft">{serverError}</p>}
      {disabled ? (
        <DocumentFileList
          files={
            generic_documents?.map((document) => ({
              id: document.attachment_id,
              fileName: getFilenameFromUrl(document?.url),
              currentFileUrl: document?.url,
            })) || []
          }
        />
      ) : (
        <MultipleDocumentSoftUpload
          title="Anexe um arquivo"
          name="generic_documents"
          files={generic_documents}
          accept=".png, .jpg, .jpeg, .webp, .pdf, .doc, .docx, .xls, .xlsx"
          uploadBlob={uploadBlob}
          deletePrivateCompanyLeadGenericDocument={deletePublicCompanyLeadGenericDocument}
          control={control}
          fieldName="generic_documents"
          disabled={
            uploadBlobIsPending || createPublicCompanyLeadIsPending || deletePublicCompanyLeadIsPending || isFetching
          }
        />
      )}
    </div>
  )
}
